import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DateFilterCalendar from './DateFilterCalendar';
import '../styles/ModelRequestsTab.css';

const API_BASE_URL = window.API_BASE_URL;

const JOBS = [
  { id: '', label: 'All' },
  { id: 'ocr', label: 'OCR' },
  { id: 'grading', label: 'Grading' },
  { id: 'rubric', label: 'Rubric' }
];

const JOB_LABEL = { ocr: 'OCR', grading: 'Grading', rubric: 'Rubric' };
const STATUS_LABEL = { finished: 'Finished', failed: 'Failed', running: 'Sending' };

function formatWhen(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  return date.toLocaleString(undefined, sameDay
    ? { hour: '2-digit', minute: '2-digit', second: '2-digit' }
    : { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatTokenCount(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString();
}

function formatDuration(ms, status) {
  if (status === 'running') return 'In progress';
  const n = Number(ms);
  if (!Number.isFinite(n) || n < 0) return '';
  if (n < 1000) return `${n} ms`;
  const seconds = n / 1000;
  if (seconds < 60) return `${seconds < 10 ? seconds.toFixed(1) : Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return `${minutes}m ${String(rest).padStart(2, '0')}s`;
}

function ForCell({ about, detail, hint }) {
  const [tip, setTip] = useState(null);
  const lines = String(hint || '').split('\n').map((line) => line.trim()).filter(Boolean);
  const open = (event) => {
    if (!lines.length) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const below = rect.bottom + 8;
    const flip = below + 220 > window.innerHeight && rect.top > 220;
    setTip({
      top: flip ? undefined : below,
      bottom: flip ? window.innerHeight - rect.top + 8 : undefined,
      left: Math.min(rect.left, window.innerWidth - 280)
    });
  };
  return (
    <td className="mr-about">
      <div
        className={lines.length ? 'mr-about-hover' : undefined}
        onMouseEnter={open}
        onMouseLeave={() => setTip(null)}
      >
        {about || '—'}
      </div>
      {detail && <div className="mr-detail">{detail}</div>}
      {tip && (
        <div className="mr-hover" style={{ top: tip.top, bottom: tip.bottom, left: tip.left }}>
          {lines.map((line, index) => (
            <div key={`${line}-${index}`} className={index === 0 ? 'mr-hover-rule' : undefined}>{line}</div>
          ))}
        </div>
      )}
    </td>
  );
}

const SORT_COLUMNS = [
  { id: 'time', label: 'Time' },
  { id: 'user', label: 'User' },
  { id: 'model', label: 'Model' },
  { id: 'machine', label: 'Machine' },
  { id: 'job', label: 'Job' },
  { id: 'where', label: 'Where' },
  { id: 'for', label: 'For' },
  { id: 'tokens', label: 'Tokens' },
  { id: 'result', label: 'Result' },
  { id: 'duration', label: 'Duration' }
];

function sortValue(row, id) {
  if (id === 'time') {
    const time = new Date(row.startedAt).getTime();
    return Number.isFinite(time) ? time : null;
  }
  if (id === 'user') return String(row.username || '');
  if (id === 'model') return shortModel(row.model);
  if (id === 'machine') return String(row.machine || '');
  if (id === 'job') return String(JOB_LABEL[row.job] || row.job || '');
  if (id === 'where') return String(row.place || '');
  if (id === 'for') return String(row.about || '');
  if (id === 'tokens') {
    if (row.inputTokens == null && row.outputTokens == null) return null;
    return (Number(row.inputTokens) || 0) + (Number(row.outputTokens) || 0);
  }
  if (id === 'result') return String(STATUS_LABEL[row.status] || row.status || '');
  if (id === 'duration') {
    if (row.status === 'running') return null;
    const n = Number(row.durationMs);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }
  return '';
}

function compareRows(a, b, id, dir) {
  const av = sortValue(a, id);
  const bv = sortValue(b, id);
  const aMissing = av == null || av === '';
  const bMissing = bv == null || bv === '';
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;
  const result = typeof av === 'number' && typeof bv === 'number'
    ? av - bv
    : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
  return dir === 'desc' ? -result : result;
}

function JobSplit({ ocr, grading, rubric }) {
  return (
    <span className="mr-class-jobs">
      <span>OCR <strong>{Number(ocr) || 0}</strong></span>
      <span>Grading <strong>{Number(grading) || 0}</strong></span>
      {Number(rubric) > 0 && <span>Rubric <strong>{Number(rubric) || 0}</strong></span>}
    </span>
  );
}

function shortModel(model) {
  const text = String(model || '');
  const slash = text.lastIndexOf('/');
  return slash >= 0 ? text.slice(slash + 1) : text;
}

function rowMatches(row, needle) {
  const haystack = [
    row.username,
    row.model,
    shortModel(row.model),
    row.machine,
    row.place,
    row.about,
    row.detail,
    row.hint
  ].join('\n').toLowerCase();
  return haystack.includes(needle);
}

function localDayKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function dayBounds(startKey, endKey) {
  if (!startKey) return { from: '', to: '' };
  const parse = (key) => {
    const [year, month, day] = key.split('-').map(Number);
    return { year, month, day };
  };
  const start = parse(startKey);
  const end = parse(endKey || startKey);
  const from = new Date(start.year, start.month - 1, start.day, 0, 0, 0, 0);
  const to = new Date(end.year, end.month - 1, end.day, 23, 59, 59, 999);
  return { from: from.toISOString(), to: to.toISOString() };
}

export default function ModelRequestsTab() {
  const [summary, setSummary] = useState([]);
  const [classes, setClasses] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(200);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [job, setJob] = useState('');
  const [model, setModel] = useState('');
  const [classLabel, setClassLabel] = useState('');
  const [sort, setSort] = useState({ id: 'time', dir: 'desc' });
  const [query, setQuery] = useState('');
  const [startDate, setStartDate] = useState(() => localDayKey(new Date()));
  const [endDate, setEndDate] = useState('');
  const [dayCounts, setDayCounts] = useState({});

  const load = useCallback(async ({ quiet } = {}) => {
    try {
      if (!quiet) setLoading(true);
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams();
      if (job) params.set('job', job);
      if (model) params.set('model', model);
      if (classLabel) params.set('classLabel', classLabel);
      const bounds = dayBounds(startDate, endDate);
      if (bounds.from) {
        params.set('from', bounds.from);
        params.set('to', bounds.to);
      }
      params.set('tz', String(new Date().getTimezoneOffset()));
      params.set('limit', '300');
      const response = await fetch(`${API_BASE_URL}/model-requests?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Failed to load model requests');
      setSummary(Array.isArray(data.summary) ? data.summary : []);
      setClasses(Array.isArray(data.classes) ? data.classes : []);
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setTotal(Number(data.total) || 0);
      setLimit(Number(data.limit) || 300);
      const counts = {};
      (Array.isArray(data.days) ? data.days : []).forEach((day) => {
        if (day && day.date) counts[day.date] = Number(day.count) || 0;
      });
      setDayCounts(counts);
      setError('');
    } catch (err) {
      if (!quiet) setError(err.message || 'Failed to load model requests');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [job, model, classLabel, startDate, endDate]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') load({ quiet: true });
    }, 5000);
    return () => clearInterval(timer);
  }, [load]);

  const running = useMemo(
    () => summary.reduce((sum, card) => sum + (Number(card.running) || 0), 0),
    [summary]
  );
  const kept = useMemo(
    () => summary.reduce((sum, card) => sum + (Number(card.total) || 0), 0),
    [summary]
  );

  const todayKey = localDayKey(new Date());
  const isAll = !startDate && !endDate;
  const isToday = startDate === todayKey && !endDate;
  const needle = query.trim().toLowerCase();
  const visibleRows = useMemo(() => {
    const matched = needle ? rows.filter((row) => rowMatches(row, needle)) : rows.slice();
    matched.sort((a, b) => compareRows(a, b, sort.id, sort.dir));
    return matched;
  }, [rows, needle, sort]);

  const chooseSort = (id) => {
    setSort((current) => {
      if (current.id === id) return { id, dir: current.dir === 'asc' ? 'desc' : 'asc' };
      return { id, dir: id === 'time' || id === 'duration' || id === 'tokens' ? 'desc' : 'asc' };
    });
  };

  return (
    <div className="mr-page">
      <header className="mr-header">
        <div>
          <h1>Model requests</h1>
          <p>
            Every OCR, grading, and rubric-chat request, kept by the model that received it.
            {running > 0 ? ` ${running} sending now.` : ''}
          </p>
        </div>
        <div className="mr-header-meta">
          <span>{kept.toLocaleString()} kept</span>
          <button type="button" className="mr-refresh" onClick={() => load()}>Refresh</button>
        </div>
      </header>

      {error && <div className="mr-error" role="alert">{error}</div>}

      <div className="mr-body">
        <aside className="mr-cal">
          <div className="mr-shortcuts" role="group" aria-label="Date shortcuts">
            <button
              type="button"
              className={isAll ? 'active' : ''}
              onClick={() => { setStartDate(''); setEndDate(''); }}
            >
              All
            </button>
            <button
              type="button"
              className={isToday ? 'active' : ''}
              onClick={() => { setStartDate(todayKey); setEndDate(''); }}
            >
              Today
            </button>
          </div>
          <DateFilterCalendar
            counts={dayCounts}
            range
            columns={5}
            selectedDate={startDate}
            selectedEndDate={endDate}
            onDateChange={(start, end = '') => {
              setStartDate(start || '');
              setEndDate(end || '');
            }}
            itemLabelSingular="Request"
            itemLabelPlural="Requests"
            title="Date range"
          />
        </aside>
        <div className="mr-main">

      <div className="mr-summaries">
      <section className="mr-cards" aria-label="Totals by model">
        {summary.length === 0 && !loading && (
          <div className="mr-empty-card">No requests yet. They appear here as soon as a model is called.</div>
        )}
        {summary.map((card) => (
          <button
            key={`${card.machine}::${card.model}`}
            type="button"
            className={`mr-card ${model === card.model ? 'active' : ''}`}
            onClick={() => setModel((current) => (current === card.model ? '' : card.model))}
            title={card.model}
          >
            <div className="mr-card-top">
              <span className="mr-card-model">{shortModel(card.model)}</span>
              <span className="mr-card-machine">{card.machine}</span>
            </div>
            <div className="mr-card-total">{card.total.toLocaleString()}</div>
            <div className="mr-card-tokens">
              {formatTokenCount(card.inputTokens)} in · {formatTokenCount(card.outputTokens)} out
            </div>
            <div className="mr-card-split">
              <span>OCR <strong>{card.ocr}</strong></span>
              <span>Grading <strong>{card.grading}</strong></span>
              <span>Rubric <strong>{card.rubric}</strong></span>
            </div>
            {card.failed > 0 && <div className="mr-card-failed">{card.failed} failed</div>}
          </button>
        ))}
      </section>
      <aside className="mr-classes" aria-label="Requests by class">
        <h2>Classes</h2>
        {classes.length === 0 && !loading && (
          <div className="mr-class-empty">No class on these requests.</div>
        )}
        {classes.map((item) => (
          <button
            key={item.label}
            type="button"
            className={`mr-class ${classLabel === item.label ? 'active' : ''}`}
            title={classLabel === item.label ? 'Show every class' : `Show only ${item.label}`}
            onClick={() => setClassLabel((current) => (current === item.label ? '' : item.label))}
          >
            <span className="mr-class-head">
              <span>{item.label}</span>
              <strong>({Number(item.count) || 0})</strong>
            </span>
            <JobSplit ocr={item.ocr} grading={item.grading} rubric={item.rubric} />
            {(item.models || []).length > 0 && (
              <span className="mr-class-models">
                {item.models.map((model) => (
                  <span
                    key={`${model.model}::${model.machine}`}
                    className="mr-class-model"
                    title={model.model}
                  >
                    <span className="mr-class-model-name">
                      <span>{shortModel(model.model)}</span>
                      {model.machine ? <em>{model.machine}</em> : null}
                      <strong>({Number(model.count) || 0})</strong>
                    </span>
                    <JobSplit ocr={model.ocr} grading={model.grading} rubric={model.rubric} />
                  </span>
                ))}
              </span>
            )}
          </button>
        ))}
      </aside>
      </div>

      <div className="mr-toolbar">
        <div className="mr-jobs" role="tablist" aria-label="Request type">
          {JOBS.map((item) => (
            <button
              key={item.id || 'all'}
              type="button"
              role="tab"
              aria-selected={job === item.id}
              className={job === item.id ? 'active' : ''}
              onClick={() => setJob(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <form className="mr-search" onSubmit={(event) => event.preventDefault()}>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Student, class, subject, user, or page"
            aria-label="Search requests"
          />
          {(query || model || job || classLabel) && (
            <button
              type="button"
              className="mr-clear"
              onClick={() => {
                setQuery('');
                setModel('');
                setJob('');
                setClassLabel('');
              }}
            >
              Clear
            </button>
          )}
        </form>
      </div>

      <div className="mr-table-wrap">
        <table className="mr-table">
          <thead>
            <tr>
              {SORT_COLUMNS.map((column) => {
                const active = sort.id === column.id;
                const dir = active ? sort.dir : 'none';
                return (
                  <th key={column.id} aria-sort={dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : 'none'}>
                    <button
                      type="button"
                      className={`mr-sort ${active ? 'active' : ''}`}
                      onClick={() => chooseSort(column.id)}
                      title={`Sort by ${column.label}`}
                    >
                      {column.label}
                      {active && <span aria-hidden="true">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr><td colSpan={10} className="mr-muted">Loading requests…</td></tr>
            )}
            {!(loading && rows.length === 0) && visibleRows.length === 0 && (
              <tr><td colSpan={10} className="mr-muted">No requests match this view.</td></tr>
            )}
            {visibleRows.map((row) => (
              <tr key={row.id}>
                <td className="mr-time">{formatWhen(row.startedAt)}</td>
                <td className="mr-user">{row.username || '—'}</td>
                <td className="mr-model" title={row.model}>{shortModel(row.model)}</td>
                <td>{row.machine}</td>
                <td><span className={`mr-job mr-job-${row.job}`}>{JOB_LABEL[row.job] || row.job}</span></td>
                <td>{row.place}</td>
                <ForCell about={row.about} detail={row.detail} hint={row.hint} />
                <td className="mr-tokens" title="Input · output">
                  {row.inputTokens == null && row.outputTokens == null
                    ? '—'
                    : `${formatTokenCount(row.inputTokens)} in · ${formatTokenCount(row.outputTokens)} out`}
                </td>
                <td>
                  <span className={`mr-status mr-status-${row.status}`} title={row.error || undefined}>
                    {STATUS_LABEL[row.status] || row.status}
                  </span>
                </td>
                <td className="mr-duration">{formatDuration(row.durationMs, row.status)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!needle && total > rows.length && (
        <p className="mr-footnote">Showing the latest {Math.min(limit, rows.length)} of {total.toLocaleString()} matching requests.</p>
      )}
        </div>
      </div>
    </div>
  );
}
